import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  EuroCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { Option } = Select;
const { Text } = Typography;

const VAT_RATE = 0.23;
const CURRENCY_SYMBOL = "€";
const CLOUD_ELIGIBLE_SIZE_IDS = new Set(["13", "14", "15"]);

const STORAGE_OPTIONS = [
  { value: "Non Ottoman", label: "No Storage" },
  { value: "Ottoman", label: "Ottoman" },
  { value: "Cloud Storage", label: "Cloud Storage" },
];

const formatMoney = (value) =>
  `${CURRENCY_SYMBOL}${Number(value || 0).toFixed(2)}`;

const normalizeStorageType = (value) => {
  const raw = String(value || "").trim().toLowerCase();

  if (
    raw === "no storage" ||
    raw === "non ottoman" ||
    raw === "non-ottoman" ||
    raw === "nonottoman"
  ) {
    return "Non Ottoman";
  }

  if (raw === "ottoman") return "Ottoman";

  if (
    raw === "cloud storage" ||
    raw === "cloud-storage" ||
    raw === "cloudstorage"
  ) {
    return "Cloud Storage";
  }

  return value || "";
};

const normalizeTypes = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map(normalizeStorageType).filter(Boolean);
  }

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(normalizeStorageType).filter(Boolean);
      }
    } catch {
      return raw
        .split(",")
        .map((item) => normalizeStorageType(item))
        .filter(Boolean);
    }
  }

  return [];
};

const getCustomerLabel = (customer) =>
  customer?.company_name ||
  customer?.name ||
  customer?.full_name ||
  customer?.email ||
  `Customer #${customer?.id}`;

const getProductCategoryLabel = (product) => {
  const categories = product?.Categories || product?.categories || [];

  if (!Array.isArray(categories) || categories.length === 0) {
    return "";
  }

  return categories
    .map((category) => category?.category_name || category?.name)
    .filter(Boolean)
    .join(", ");
};

const isBedProduct = (product) => {
  const categories = product?.Categories || product?.categories || [];

  return categories.some(
    (category) =>
      String(category?.category_name || category?.name || "").toLowerCase() ===
      "beds"
  );
};

const getProductStockLabel = (product) => {
  if (product?.isBed || isBedProduct(product)) {
    return "Made to order";
  }

  const stock = Number(product?.stock || 0);
  return `Stock: ${stock}`;
};

const isCloudEligibleSize = (sizeId) =>
  CLOUD_ELIGIBLE_SIZE_IDS.has(String(sizeId));

const getSizeObject = (productDetails, sizeId) => {
  const sizes = productDetails?.sizes || productDetails?.Sizes || [];

  return sizes.find((size) => String(size.id) === String(sizeId)) || null;
};

const getOttomanSalePrice = (productDetails, sizeId) => {
  const fromPricing = Number(
    productDetails?.storagePricing?.ottomanPrices?.[String(sizeId)] || 0
  );

  if (fromPricing > 0) {
    return fromPricing;
  }

  const size = getSizeObject(productDetails, sizeId);

  return Number(size?.ottoman_price || 0);
};

const pickVariation = (
  productDetails,
  sizeId,
  storageType,
  fabricId,
  fabricColorId
) => {
  const variations = productDetails?.variations || productDetails?.Variations || [];

  if (!Array.isArray(variations) || variations.length === 0 || !sizeId) {
    return null;
  }

  const sizeNum = Number(sizeId);
  const finalStorageType = storageType ? normalizeStorageType(storageType) : null;

  let pool = variations.filter((variation) => {
    const sameSize = Number(variation.sizeId) === sizeNum;

    if (!sameSize) return false;

    if (finalStorageType) {
      const sameStorage =
        normalizeStorageType(variation.storage_type).toLowerCase() ===
        finalStorageType.toLowerCase();

      if (!sameStorage) return false;
    }

    if (fabricId != null) {
      if (Number(variation.fabricId) !== Number(fabricId)) return false;
    }

    if (fabricColorId != null) {
      if (Number(variation.fabricColorId) !== Number(fabricColorId)) {
        return false;
      }
    }

    return true;
  });

  if (!pool.length && fabricId != null && fabricColorId != null) {
    pool = variations.filter(
      (variation) =>
        Number(variation.sizeId) === sizeNum &&
        Number(variation.fabricId) === Number(fabricId) &&
        Number(variation.fabricColorId) === Number(fabricColorId)
    );
  }

  if (!pool.length) {
    pool = variations.filter((variation) => Number(variation.sizeId) === sizeNum);
  }

  if (!pool.length) return null;

  pool.sort((a, b) => Number(a.price_euro || 0) - Number(b.price_euro || 0));

  return pool[0] || null;
};

const calculateUnitPrice = (productDetails, line) => {
  if (!productDetails) {
    return Number(line.unitPrice || 0);
  }

  const bed = productDetails.isBed || isBedProduct(productDetails);

  if (!line.sizeId) {
    return Number(productDetails.price_euro || 0);
  }

  if (!bed) {
    const variation = pickVariation(productDetails, line.sizeId, null, null, null);

    return Number(variation?.price_euro ?? productDetails.price_euro ?? 0);
  }

  const baseVariation = pickVariation(
    productDetails,
    line.sizeId,
    "Non Ottoman",
    line.fabricId,
    line.fabricColorId
  );

  const basePrice = Number(
    baseVariation?.price_euro ?? productDetails.price_euro ?? 0
  );

  const ottomanSalePrice = getOttomanSalePrice(productDetails, line.sizeId);
  const cloudBase = Number(productDetails?.storagePricing?.cloudBase || 0);

  const storageType = normalizeStorageType(line.storageType);

  if (storageType === "Ottoman") {
    return Number((basePrice + ottomanSalePrice).toFixed(2));
  }

  if (storageType === "Cloud Storage") {
    if (!isCloudEligibleSize(line.sizeId)) {
      return Number(basePrice.toFixed(2));
    }

    return Number((basePrice + ottomanSalePrice + cloudBase).toFixed(2));
  }

  return Number(basePrice.toFixed(2));
};

const emptyLine = () => ({
  key: Date.now() + Math.random(),
  productId: null,
  sizeId: null,
  storageType: null,
  fabricId: null,
  fabricColorId: null,
  quantity: 1,
  unitPrice: 0,
});

const getLineSubtotal = (line) =>
  Number(line.quantity || 0) * Number(line.unitPrice || 0);

const getLineVat = (line) => getLineSubtotal(line) * VAT_RATE;

const getLineTotalWithVat = (line) => getLineSubtotal(line) + getLineVat(line);

const SectionHeader = ({ icon, title, right }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 20px",
      borderBottom: "1px solid #f0f0f0",
      background: "#fff",
      borderRadius: "10px 10px 0 0",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: "#eff6ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#2563eb",
          fontSize: 15,
        }}
      >
        {icon}
      </span>

      <Text strong style={{ fontSize: 14, color: "#111" }}>
        {title}
      </Text>
    </div>

    {right}
  </div>
);

const Section = ({ icon, title, right, children, style }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 10,
      border: "1px solid #e8ecf0",
      marginBottom: 16,
      overflow: "hidden",
      ...style,
    }}
  >
    <SectionHeader icon={icon} title={title} right={right} />
    <div style={{ padding: "20px" }}>{children}</div>
  </div>
);

const StatPill = ({ label, value, color }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "10px 16px",
      background: "#f8faff",
      borderRadius: 8,
      border: "1px solid #e0eaff",
      minWidth: 120,
    }}
  >
    <Text
      style={{
        fontSize: 11,
        color: "#8896b0",
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </Text>

    <Text
      strong
      style={{
        fontSize: 17,
        color: color || "#111",
        marginTop: 2,
      }}
    >
      {value}
    </Text>
  </div>
);

const FieldLabel = ({ children, required, tip }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
    <Text style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>
      {required && <span style={{ color: "#ef4444", marginRight: 2 }}>*</span>}
      {children}
    </Text>

    {tip && (
      <Tooltip title={tip}>
        <InfoCircleOutlined
          style={{
            color: "#9ca3af",
            fontSize: 13,
            cursor: "help",
          }}
        />
      </Tooltip>
    )}
  </div>
);

const AdminCreateCustomerOrder = () => {
  const [form] = Form.useForm();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productOptions, setProductOptions] = useState({});
  const [lines, setLines] = useState([emptyLine()]);

  const selectedCustomerId = Form.useWatch("customerId", form);
  const urgentWatch = Form.useWatch("urgent", form);

  const selectedCustomer = useMemo(
    () =>
      customers.find(
        (customer) => String(customer.id) === String(selectedCustomerId)
      ),
    [customers, selectedCustomerId]
  );

  const subtotalAmount = useMemo(
    () => lines.reduce((sum, line) => sum + getLineSubtotal(line), 0),
    [lines]
  );

  const vatAmount = useMemo(() => subtotalAmount * VAT_RATE, [subtotalAmount]);

  const totalAmount = useMemo(
    () => subtotalAmount + vatAmount,
    [subtotalAmount, vatAmount]
  );

  const creditAvailable = Number(selectedCustomer?.credit_limit || 0);
  const creditOk = !selectedCustomer || creditAvailable >= totalAmount;

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);

      try {
        const [customersResponse, productsResponse] = await Promise.all([
          axiosInstance.get("/admin/customer-order/customers"),
          axiosInstance.get("/admin/customer-order/products"),
        ]);

        setCustomers(
          Array.isArray(customersResponse.data?.customers)
            ? customersResponse.data.customers
            : []
        );

        setProducts(
          Array.isArray(productsResponse.data?.products)
            ? productsResponse.data.products
            : []
        );
      } catch (error) {
        message.error(error?.response?.data?.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const patchLine = (key, patch) => {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...patch } : line))
    );
  };

  const loadProductOptions = async (productId) => {
    if (!productId) return null;

    if (productOptions[productId]) {
      return productOptions[productId];
    }

    const { data } = await axiosInstance.get(
      `/admin/customer-order/products/${productId}/options`
    );

    const product = data?.product;

    if (!product) {
      throw new Error("Product details not found");
    }

    setProductOptions((prev) => ({
      ...prev,
      [productId]: product,
    }));

    return product;
  };

  const refreshLinePrice = (key, nextLine) => {
    const productDetails = productOptions[nextLine.productId];

    if (!productDetails) return;

    patchLine(key, {
      ...nextLine,
      unitPrice: calculateUnitPrice(productDetails, nextLine),
    });
  };

  const handleProductChange = async (key, productId) => {
    patchLine(key, {
      productId,
      sizeId: null,
      storageType: null,
      fabricId: null,
      fabricColorId: null,
      unitPrice: 0,
    });

    try {
      const productDetails = await loadProductOptions(productId);
      const bed = productDetails?.isBed || isBedProduct(productDetails);

      const firstSize =
        (productDetails?.sizes || productDetails?.Sizes || [])[0] || null;

      const firstFabric =
        bed && Array.isArray(productDetails?.fabrics)
          ? productDetails.fabrics[0]
          : null;

      const firstColor =
        firstFabric && Array.isArray(firstFabric.fabricColors)
          ? firstFabric.fabricColors[0]
          : null;

      const nextLine = {
        key,
        productId,
        sizeId: firstSize?.id || null,
        storageType: bed ? "Non Ottoman" : null,
        fabricId: firstFabric?.id || null,
        fabricColorId: firstColor?.id || null,
        quantity: 1,
        unitPrice: 0,
      };

      nextLine.unitPrice = calculateUnitPrice(productDetails, nextLine);

      patchLine(key, nextLine);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to load product options"
      );
    }
  };

  const handleCustomerChange = (id) => {
    const customer = customers.find((item) => String(item.id) === String(id));

    form.setFieldsValue({
      shipping_address:
        customer?.shipping_address ||
        customer?.display_address ||
        customer?.billing_address ||
        "",
    });
  };

  const validateLines = () => {
    for (const [index, line] of lines.entries()) {
      const productDetails =
        productOptions[line.productId] ||
        products.find(
          (product) => String(product.id) === String(line.productId)
        );

      const bed = productDetails?.isBed || isBedProduct(productDetails);

      if (!line.productId) {
        return `Line ${index + 1}: select a product`;
      }

      if (!line.sizeId) {
        return `Line ${index + 1}: select a size`;
      }

      if (!line.quantity || Number(line.quantity) < 1) {
        return `Line ${index + 1}: quantity must be at least 1`;
      }

      if (Number(line.unitPrice || 0) <= 0) {
        return `Line ${index + 1}: unit price is missing or invalid`;
      }

      if (bed) {
        if (!line.storageType) {
          return `Line ${index + 1}: select storage type`;
        }

        if (
          normalizeStorageType(line.storageType) === "Cloud Storage" &&
          !isCloudEligibleSize(line.sizeId)
        ) {
          return `Line ${index + 1}: Cloud Storage is not available for selected size`;
        }

        if (!line.fabricId) {
          return `Line ${index + 1}: select fabric`;
        }

        if (!line.fabricColorId) {
          return `Line ${index + 1}: select fabric color`;
        }
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const lineError = validateLines();

      if (lineError) {
        message.error(lineError);
        return;
      }

      setSaving(true);

      await axiosInstance.post("/admin/customer-order", {
        customerId: values.customerId,
        shipping_address: values.shipping_address,
        referenceNumber: values.referenceNumber,
        notes: values.notes,
        urgent: values.urgent,
        overrideCreditLimit: values.overrideCreditLimit,
        vatRate: VAT_RATE,
        items: lines.map((line) => {
          const unitPriceExVat = Number(line.unitPrice || 0);
          const unitVat = Number((unitPriceExVat * VAT_RATE).toFixed(2));
          const unitPriceWithVat = Number((unitPriceExVat + unitVat).toFixed(2));

          return {
            productId: line.productId,
            sizeId: line.sizeId,
            storageType: line.storageType,
            fabricId: line.fabricId,
            fabricColorId: line.fabricColorId,
            quantity: Number(line.quantity || 1),

            unitPrice: unitPriceExVat,
            unitPriceExVat,

            vatRate: VAT_RATE,
            unitVat,
            unitPriceWithVat,
          };
        }),
      });

      message.success("Order created successfully");

      form.resetFields();
      setLines([emptyLine()]);
    } catch (error) {
      if (error?.errorFields) return;

      message.error(error?.response?.data?.message || "Failed to create order");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: <span style={colHead}>Product</span>,
      width: 290,
      render: (_, line) => (
        <Select
          showSearch
          placeholder="Search product…"
          value={line.productId}
          optionFilterProp="searchtext"
          optionLabelProp="displaylabel"
          style={{ width: "100%" }}
          size="small"
          onChange={(value) => handleProductChange(line.key, value)}
          filterOption={(input, option) =>
            String(option?.searchtext || "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {products.map((product) => {
            const categoryLabel = getProductCategoryLabel(product);
            const stockLabel = getProductStockLabel(product);
            const bed = product?.isBed || isBedProduct(product);

            return (
              <Option
                key={product.id}
                value={product.id}
                displaylabel={product.product_name}
                searchtext={`${product.product_name || ""} ${
                  product.sku || ""
                } ${categoryLabel} ${stockLabel}`}
              >
                <div style={{ lineHeight: 1.35, padding: "4px 0" }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#111827",
                    }}
                  >
                    {product.product_name}
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    {categoryLabel && (
                      <span
                        style={{
                          fontSize: 11,
                          color: "#2563eb",
                          background: "#eff6ff",
                          border: "1px solid #dbeafe",
                          borderRadius: 999,
                          padding: "1px 7px",
                          fontWeight: 600,
                        }}
                      >
                        {categoryLabel}
                      </span>
                    )}

                    <span
                      style={{
                        fontSize: 11,
                        color: bed
                          ? "#7c3aed"
                          : Number(product?.stock || 0) > 0
                          ? "#16a34a"
                          : "#dc2626",
                        background: bed
                          ? "#f3e8ff"
                          : Number(product?.stock || 0) > 0
                          ? "#f0fdf4"
                          : "#fef2f2",
                        border: bed
                          ? "1px solid #e9d5ff"
                          : Number(product?.stock || 0) > 0
                          ? "1px solid #bbf7d0"
                          : "1px solid #fecaca",
                        borderRadius: 999,
                        padding: "1px 7px",
                        fontWeight: 600,
                      }}
                    >
                      {stockLabel}
                    </span>
                  </div>

                  {product.sku && (
                    <div
                      style={{
                        marginTop: 2,
                        fontSize: 11,
                        color: "#94a3b8",
                      }}
                    >
                      SKU: {product.sku}
                    </div>
                  )}
                </div>
              </Option>
            );
          })}
        </Select>
      ),
    },
    {
      title: <span style={colHead}>Size</span>,
      width: 135,
      render: (_, line) => {
        const productDetails = productOptions[line.productId];

        return (
          <Select
            placeholder="—"
            value={line.sizeId}
            size="small"
            disabled={!line.productId || !productDetails}
            style={{ width: "100%" }}
            onChange={(value) => {
              const nextLine = {
                ...line,
                sizeId: value,
                storageType:
                  normalizeStorageType(line.storageType) === "Cloud Storage" &&
                  !isCloudEligibleSize(value)
                    ? "Non Ottoman"
                    : line.storageType,
              };

              refreshLinePrice(line.key, nextLine);
            }}
          >
            {(productDetails?.sizes || productDetails?.Sizes || []).map((size) => (
              <Option key={size.id} value={size.id}>
                {size.size}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: <span style={colHead}>Storage</span>,
      width: 155,
      render: (_, line) => {
        const productDetails = productOptions[line.productId];
        const bed = productDetails?.isBed || isBedProduct(productDetails);

        if (line.productId && productDetails && !bed) {
          return <span style={naTag}>N/A</span>;
        }

        const productTypes = normalizeTypes(productDetails?.types);
        const allowedOptions = productTypes.length
          ? STORAGE_OPTIONS.filter((option) => productTypes.includes(option.value))
          : STORAGE_OPTIONS;

        return (
          <Select
            placeholder="—"
            value={line.storageType}
            size="small"
            disabled={!bed}
            style={{ width: "100%" }}
            onChange={(value) => {
              const nextLine = {
                ...line,
                storageType: value,
              };

              refreshLinePrice(line.key, nextLine);
            }}
          >
            {allowedOptions.map((option) => {
              const disabled =
                option.value === "Cloud Storage" &&
                (!line.sizeId || !isCloudEligibleSize(line.sizeId));

              return (
                <Option
                  key={option.value}
                  value={option.value}
                  disabled={disabled}
                >
                  {option.label}
                </Option>
              );
            })}
          </Select>
        );
      },
    },
    {
      title: <span style={colHead}>Fabric</span>,
      width: 150,
      render: (_, line) => {
        const productDetails = productOptions[line.productId];
        const bed = productDetails?.isBed || isBedProduct(productDetails);

        if (line.productId && productDetails && !bed) {
          return <span style={naTag}>N/A</span>;
        }

        return (
          <Select
            placeholder="—"
            value={line.fabricId}
            size="small"
            disabled={!bed}
            style={{ width: "100%" }}
            onChange={(value) => {
              const selectedFabric = (productDetails?.fabrics || []).find(
                (fabric) => String(fabric.id) === String(value)
              );

              const firstColor =
                selectedFabric?.fabricColors?.length > 0
                  ? selectedFabric.fabricColors[0]
                  : null;

              const nextLine = {
                ...line,
                fabricId: value,
                fabricColorId: firstColor?.id || null,
              };

              refreshLinePrice(line.key, nextLine);
            }}
          >
            {(productDetails?.fabrics || []).map((fabric) => (
              <Option key={fabric.id} value={fabric.id}>
                {fabric.fabric_name}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: <span style={colHead}>Color</span>,
      width: 145,
      render: (_, line) => {
        const productDetails = productOptions[line.productId];
        const bed = productDetails?.isBed || isBedProduct(productDetails);

        if (line.productId && productDetails && !bed) {
          return <span style={naTag}>N/A</span>;
        }

        const fabric = (productDetails?.fabrics || []).find(
          (item) => String(item.id) === String(line.fabricId)
        );

        return (
          <Select
            placeholder="—"
            value={line.fabricColorId}
            size="small"
            disabled={!line.fabricId}
            style={{ width: "100%" }}
            onChange={(value) => {
              const nextLine = {
                ...line,
                fabricColorId: value,
              };

              refreshLinePrice(line.key, nextLine);
            }}
          >
            {(fabric?.fabricColors || []).map((color) => (
              <Option key={color.id} value={color.id}>
                {color.color_name}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: <span style={colHead}>Qty</span>,
      width: 80,
      render: (_, line) => (
        <InputNumber
          min={1}
          value={line.quantity}
          size="small"
          style={{ width: "100%" }}
          onChange={(value) =>
            patchLine(line.key, {
              quantity: value || 1,
            })
          }
        />
      ),
    },
    {
      title: <span style={colHead}>Unit Price</span>,
      width: 125,
      render: (_, line) => (
        <InputNumber
          min={0}
          step={0.01}
          precision={2}
          value={line.unitPrice}
          size="small"
          style={{ width: "100%" }}
          prefix={CURRENCY_SYMBOL}
          onChange={(value) =>
            patchLine(line.key, {
              unitPrice: Number(value || 0),
            })
          }
        />
      ),
    },
    {
      title: <span style={colHead}>VAT 23%</span>,
      width: 105,
      render: (_, line) => (
        <span style={{ fontWeight: 600, color: "#64748b", fontSize: 13 }}>
          {formatMoney(getLineVat(line))}
        </span>
      ),
    },
    {
      title: <span style={colHead}>Total Inc. VAT</span>,
      width: 130,
      render: (_, line) => (
        <span style={{ fontWeight: 700, color: "#2563eb", fontSize: 13 }}>
          {formatMoney(getLineTotalWithVat(line))}
        </span>
      ),
    },
    {
      title: "",
      width: 42,
      render: (_, line) => (
        <Tooltip title="Remove">
          <Button
            danger
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            disabled={lines.length === 1}
            onClick={() =>
              setLines((prev) =>
                prev.length === 1
                  ? prev
                  : prev.filter((item) => item.key !== line.key)
              )
            }
          />
        </Tooltip>
      ),
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 360,
          gap: 16,
        }}
      >
        <Spin size="large" />

        <Text type="secondary" style={{ fontSize: 13 }}>
          Loading customers & products…
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px 28px",
        maxWidth: 1440,
        margin: "0 auto",
        fontFamily: "inherit",
      }}
    >
      <style>{styles}</style>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 22,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              <ShoppingCartOutlined />
            </div>

            <h2
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 600,
                color: "#111827",
              }}
            >
              Create Order for Customer
            </h2>
          </div>

          <Text type="secondary" style={{ fontSize: 13, paddingLeft: 46 }}>
            Select a customer, add product lines, then submit. The order will
            appear in their dashboard.
          </Text>
        </div>
      </div>

      <Form form={form} layout="vertical" requiredMark={false}>
        <Section icon={<UserOutlined />} title="Customer & Reference">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0 20px",
            }}
          >
            <Form.Item
              name="customerId"
              style={{ marginBottom: 16 }}
              rules={[{ required: true, message: "Please select a customer" }]}
              label={<FieldLabel required>Customer / Company</FieldLabel>}
            >
              <Select
                showSearch
                size="large"
                placeholder="Search by name, company or email…"
                optionFilterProp="searchtext"
                onChange={handleCustomerChange}
                optionLabelProp="label"
                dropdownStyle={{ minWidth: 380 }}
                filterOption={(input, option) =>
                  String(option?.searchtext || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {customers.map((customer) => (
                  <Option
                    key={customer.id}
                    value={customer.id}
                    label={getCustomerLabel(customer)}
                    searchtext={`${getCustomerLabel(customer)} ${
                      customer.email || ""
                    } ${customer.phone_number || ""}`}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "4px 0",
                      }}
                    >
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: "#dbeafe",
                          color: "#1d4ed8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {getCustomerLabel(customer).charAt(0).toUpperCase()}
                      </div>

                      <div style={{ lineHeight: 1.35 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#111",
                          }}
                        >
                          {getCustomerLabel(customer)}
                        </div>

                        <div style={{ fontSize: 11, color: "#94a3b8" }}>
                          {customer.email || "No email"}
                        </div>
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="referenceNumber"
              style={{ marginBottom: 16 }}
              label={
                <FieldLabel tip="Leave blank to auto-generate based on customer name">
                  PO / Reference Number
                </FieldLabel>
              }
            >
              <Input
                size="large"
                placeholder="Auto-generated if left blank"
                prefix={<FileTextOutlined style={{ color: "#cbd5e1" }} />}
              />
            </Form.Item>
          </div>

          {selectedCustomer && (
            <div
              style={{
                marginTop: 4,
                padding: "14px 16px",
                background: "linear-gradient(135deg,#f0f7ff 0%,#f5f3ff 100%)",
                border: "1px solid #c7d7f8",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  background: "#2563eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 16,
                  flexShrink: 0,
                }}
              >
                {getCustomerLabel(selectedCustomer).charAt(0).toUpperCase()}
              </div>

              <div style={{ flex: 1, minWidth: 140 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#1e3a5f",
                  }}
                >
                  {getCustomerLabel(selectedCustomer)}
                </div>

                <div style={{ fontSize: 12, color: "#6b87b0" }}>
                  {selectedCustomer.email}
                  {selectedCustomer.phone_number &&
                    ` · ${selectedCustomer.phone_number}`}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <StatPill
                  label="Credit Available"
                  value={formatMoney(creditAvailable)}
                  color={creditOk ? "#16a34a" : "#dc2626"}
                />

                {selectedCustomer.credit_terms && (
                  <StatPill
                    label="Credit Terms"
                    value={selectedCustomer.credit_terms}
                  />
                )}
              </div>

              {!creditOk && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    background: "#fef2f2",
                    borderRadius: 7,
                    border: "1px solid #fca5a5",
                  }}
                >
                  <InfoCircleOutlined
                    style={{ color: "#dc2626", fontSize: 13 }}
                  />

                  <Text
                    style={{
                      fontSize: 12,
                      color: "#dc2626",
                      fontWeight: 500,
                    }}
                  >
                    Insufficient credit — enable override in options below
                  </Text>
                </div>
              )}
            </div>
          )}
        </Section>

        <Section icon={<EnvironmentOutlined />} title="Shipping Address">
          <Form.Item
            name="shipping_address"
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: "Shipping address is required" }]}
            label={<FieldLabel required>Delivery Address</FieldLabel>}
          >
            <Input.TextArea
              rows={3}
              style={{ resize: "none", fontSize: 14 }}
              placeholder="Auto-filled from customer profile when a customer is selected. Edit if needed."
            />
          </Form.Item>
        </Section>

        <Section
          icon={<ShoppingCartOutlined />}
          title={
            <span>
              Order Lines
              <span
                style={{
                  marginLeft: 8,
                  background: "#dbeafe",
                  color: "#1d4ed8",
                  borderRadius: 20,
                  padding: "1px 9px",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {lines.length}
              </span>
            </span>
          }
          right={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
              style={{ borderRadius: 7, fontWeight: 500 }}
            >
              Add Product Line
            </Button>
          }
        >
          <Table
            rowKey="key"
            columns={columns}
            dataSource={lines}
            pagination={false}
            scroll={{ x: 1400 }}
            size="small"
            className="order-lines-table"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text type="secondary">
                      No product lines yet — click "Add Product Line" to start
                    </Text>
                  }
                />
              ),
            }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 24,
              marginTop: 14,
              padding: "12px 16px",
              background: "#f8faff",
              borderRadius: 8,
              border: "1px solid #e0eaff",
              flexWrap: "wrap",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={summaryLabelStyle}>Lines</div>
              <div style={summaryValueStyle}>{lines.length}</div>
            </div>

            <div style={dividerStyle} />

            <div style={{ textAlign: "center" }}>
              <div style={summaryLabelStyle}>Total Units</div>
              <div style={summaryValueStyle}>
                {lines.reduce(
                  (sum, line) => sum + Number(line.quantity || 0),
                  0
                )}
              </div>
            </div>

            <div style={dividerStyle} />

            <div style={{ textAlign: "right" }}>
              <div style={summaryLabelStyle}>Subtotal</div>
              <div style={summaryValueStyle}>{formatMoney(subtotalAmount)}</div>
            </div>

            <div style={dividerStyle} />

            <div style={{ textAlign: "right" }}>
              <div style={summaryLabelStyle}>VAT 23%</div>
              <div style={summaryValueStyle}>{formatMoney(vatAmount)}</div>
            </div>

            <div style={dividerStyle} />

            <div style={{ textAlign: "right" }}>
              <div style={summaryLabelStyle}>Order Total Inc. VAT</div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#2563eb",
                }}
              >
                {formatMoney(totalAmount)}
              </div>
            </div>
          </div>
        </Section>

        <Section
          icon={<FileTextOutlined />}
          title="Notes & Options"
          style={{ marginBottom: 24 }}
        >
          <Form.Item
            name="notes"
            style={{ marginBottom: 18 }}
            label={<FieldLabel>Order Notes</FieldLabel>}
          >
            <Input.TextArea
              rows={3}
              style={{ resize: "none", fontSize: 14 }}
              placeholder="Internal notes or customer-facing remarks (optional)"
            />
          </Form.Item>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <Form.Item
              name="urgent"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  cursor: "pointer",
                  padding: "14px 16px",
                  borderRadius: 9,
                  background: urgentWatch ? "#fffbeb" : "#fafafa",
                  border: `1px solid ${urgentWatch ? "#fbbf24" : "#e5e7eb"}`,
                  transition: "all .2s",
                }}
              >
                <Checkbox style={{ marginTop: 2 }} />

                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <ThunderboltOutlined
                      style={{ color: "#d97706", fontSize: 14 }}
                    />

                    <Text strong style={{ fontSize: 13, color: "#111" }}>
                      Mark as Urgent
                    </Text>
                  </div>

                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Flags this order for priority processing
                  </Text>
                </div>
              </label>
            </Form.Item>

            <Form.Item
              name="overrideCreditLimit"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  cursor: "pointer",
                  padding: "14px 16px",
                  borderRadius: 9,
                  background: !creditOk ? "#fff1f2" : "#fafafa",
                  border: `1px solid ${!creditOk ? "#fca5a5" : "#e5e7eb"}`,
                  transition: "all .2s",
                }}
              >
                <Checkbox style={{ marginTop: 2 }} />

                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <EuroCircleOutlined
                      style={{
                        color: !creditOk ? "#dc2626" : "#6b7280",
                        fontSize: 14,
                      }}
                    />

                    <Text strong style={{ fontSize: 13, color: "#111" }}>
                      Override Credit Limit
                    </Text>
                  </div>

                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Allow order even if customer credit is insufficient
                  </Text>
                </div>
              </label>
            </Form.Item>
          </div>
        </Section>

        <div
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 20,
            background: "#fff",
            borderTop: "1px solid #e8ecf0",
            padding: "14px 20px",
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 12,
            boxShadow: "0 -4px 20px rgba(0,0,0,0.05)",
            borderRadius: "0 0 10px 10px",
          }}
        >
          <Space size={10}>
            <Button
              size="large"
              icon={<ReloadOutlined />}
              style={{ borderRadius: 8, fontWeight: 500 }}
              onClick={() => {
                form.resetFields();
                setLines([emptyLine()]);
              }}
            >
              Reset
            </Button>

            <Button
              type="primary"
              size="large"
              loading={saving}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
              style={{
                borderRadius: 8,
                fontWeight: 600,
                minWidth: 210,
                background: "#2563eb",
                borderColor: "#2563eb",
                boxShadow: "0 2px 8px rgba(37,99,235,0.35)",
              }}
            >
              {saving ? "Creating Order…" : "Create Order for Customer"}
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

const colHead = {
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const naTag = {
  display: "inline-block",
  padding: "2px 8px",
  background: "#f1f5f9",
  color: "#94a3b8",
  borderRadius: 5,
  fontSize: 12,
  fontWeight: 500,
};

const summaryLabelStyle = {
  fontSize: 11,
  color: "#94a3b8",
  fontWeight: 500,
  textTransform: "uppercase",
};

const summaryValueStyle = {
  fontSize: 16,
  fontWeight: 600,
  color: "#374151",
};

const dividerStyle = {
  width: 1,
  height: 28,
  background: "#e2e8f0",
};

const styles = `
  .order-lines-table .ant-table {
    border: 1px solid #e8ecf0 !important;
    border-radius: 8px !important;
    overflow: hidden;
  }

  .order-lines-table .ant-table-thead > tr > th {
    background: #f8fafc !important;
    border-bottom: 1px solid #e2e8f0 !important;
    padding: 8px 10px !important;
    font-size: 12px !important;
  }

  .order-lines-table .ant-table-tbody > tr > td {
    padding: 7px 10px !important;
    border-bottom: 1px solid #f1f5f9 !important;
    vertical-align: middle;
  }

  .order-lines-table .ant-table-tbody > tr:hover > td {
    background: #eff6ff !important;
  }

  .order-lines-table .ant-table-tbody > tr:last-child > td {
    border-bottom: none !important;
  }

  .order-lines-table .ant-select-selector,
  .order-lines-table .ant-input-number {
    border-radius: 6px !important;
    border-color: #e2e8f0 !important;
    font-size: 13px !important;
  }

  .order-lines-table .ant-select-selector:hover,
  .order-lines-table .ant-input-number:hover {
    border-color: #93c5fd !important;
  }
`;

export default AdminCreateCustomerOrder;